/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Card from "@mui/material/Card";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";
import VuiButton from "components/VuiButton";

import { useAppAlert } from "hooks/useAppAlert";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Data
import { useEffect, useState, useMemo } from "react";
import axiosInstance from "config/axiosInstance";
import { getCurrentUser } from "utils/auth";
import { Chip, TextField } from "@mui/material";
import YapeLogo from "assets/images/logos/yape.svg";
import PlinLogo from "assets/images/logos/plin.svg";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { toPeruDateInputValue, toPeruFirstDayOfMonth } from "utils/datePeru";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const WEEKDAYS = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

function Tables() {
  const { showAlert, AlertBanner } = useAppAlert();

  // Goals state: map weekday index (0=Sun..6=Sat) -> amount (number)
  const [goals, setGoals] = useState(() => {
    try {
      const user = getCurrentUser();
      const key = user && user.id ? `goals_${user.id}` : "goals_anonymous";
      return JSON.parse(localStorage.getItem(key) || "{}");
    } catch (e) {
      return {};
    }
  });

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const [startDate, setStartDate] = useState(() => toPeruFirstDayOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => toPeruDateInputValue(new Date()));
  const [goalRows, setGoalRows] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [bancoFiltro, setBancoFiltro] = useState(""); // Estado para filtro de banco

  const saveGoals = (newGoals) => {
    setGoals(newGoals);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const key = user && user.id ? `goals_${user.id}` : "goals_anonymous";
      localStorage.setItem(key, JSON.stringify(newGoals));
    } catch (e) {
      // Silent error
    }
  };

  const fetchDepositsAndCompare = async () => {
    setLoadingGoals(true);
    try {
      const res = await axiosInstance.get("/dashboard/depositos", {
        params: { fechaInicio: startDate, fechaFin: endDate },
      });

      if (!res.data?.success) {
        showAlert("Error al obtener depósitos", "error");
        setLoadingGoals(false);
        return;
      }

      const deposits = res.data.depositos || res.data.depositos || [];

      // Aplicar filtro de banco si está activo
      const depositosFiltrados = bancoFiltro 
        ? deposits.filter(d => d.dominio === bancoFiltro)
        : deposits;

      // Aggregate sums by date (YYYY-MM-DD) using local date components to avoid
      // timezone shifts (MySQL DATETIME strings like 'YYYY-MM-DD HH:mm:ss' can be
      // interpreted as UTC by the browser and produce the previous day in some
      // timezones). We parse components and build a local date key.
      const sumsByDate = {};

      const toLocalDateKey = (mysqlDatetime) => {
        if (!mysqlDatetime) return null;
        // Expect formats like '2025-11-06 14:23:00' or '2025-11-06'
        const parts = mysqlDatetime.split(/[- :T]/).map((p) => p && p.trim());
        const year = Number(parts[0]);
        const month = Number(parts[1]);
        const day = Number(parts[2]);
        const hour = Number(parts[3] || 0);
        const minute = Number(parts[4] || 0);
        const second = Number(parts[5] || 0);

        // Create a Date using local timezone from components
        const dt = new Date(year, month - 1, day, hour, minute, second);

        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      for (const d of depositosFiltrados) {
        const key = toLocalDateKey(d.fecha);
        if (!key) continue;
        sumsByDate[key] = (sumsByDate[key] || 0) + parseFloat(d.monto || 0);
      }

      // Build rows for each date in range.
      // Parse start/end as local dates to avoid timezone shifts caused by
      // `new Date('YYYY-MM-DD')` which is treated as UTC in many browsers.
      const parseYMDLocal = (s) => {
        if (!s) return null;
        const [y, m, d] = s.split("-").map((v) => Number(v));
        return new Date(y, (m || 1) - 1, d || 1);
      };

      const start = parseYMDLocal(startDate);
      const end = parseYMDLocal(endDate);
      const rowsOut = [];
      for (let temp = new Date(start); temp <= end; temp.setDate(temp.getDate() + 1)) {
        const yyyy = temp.getFullYear();
        const mm = String(temp.getMonth() + 1).padStart(2, "0");
        const dd = String(temp.getDate()).padStart(2, "0");
        const iso = `${yyyy}-${mm}-${dd}`;
        const weekday = temp.getDay();
        const goal = Number(goals[weekday] || 0);
        const total = Number((sumsByDate[iso] || 0).toFixed(2));
        const achieved = goal > 0 ? total >= goal : null; // null means no goal set
        const diff = goal > 0 ? Number((goal - total).toFixed(2)) : null; // positive means shortfall
        const progress = goal > 0 ? Math.min(100, Math.round((total / goal) * 100)) : 0;

        // Create Estado element with color
        let estadoEl = null;
        if (achieved === null) {
          estadoEl = (
            <VuiTypography variant="button" color="text" fontWeight="medium">
              Sin objetivo
            </VuiTypography>
          );
        } else if (achieved) {
          estadoEl = (
            <VuiTypography variant="button" sx={{ color: "#4caf50" }} fontWeight="medium">
              Cumplido
            </VuiTypography>
          );
        } else {
          estadoEl = (
            <VuiTypography variant="button" sx={{ color: "#ff4d4f" }} fontWeight="medium">
              No cumplido
            </VuiTypography>
          );
        }

        // Progress element (percentage + bar)
        const progressEl = (
          <VuiBox display="flex" flexDirection="column" alignItems="flex-start">
            <VuiTypography variant="button" color="white" fontWeight="medium" mb="4px">
              {progress}%
            </VuiTypography>
            <VuiBox width="8rem">
              <VuiProgress
                value={progress}
                color={progress >= 100 ? "success" : "info"}
                sx={{ background: "#2D2E5F" }}
                label={false}
              />
            </VuiBox>
          </VuiBox>
        );

        rowsOut.push({
          Fecha: iso,
          Día: WEEKDAYS[weekday],
          Objetivo: goal > 0 ? `S/. ${formatCurrency(goal)}` : "-",
          Total: `S/. ${formatCurrency(total)}`,
          Diferencia: diff === null ? "-" : `S/. ${formatCurrency(diff)}`,
          Progreso: progressEl,
          Estado: estadoEl,
        });
      }

      setGoalRows(rowsOut);
    } catch (err) {
      showAlert("Error consultando depósitos", "error");
    } finally {
      setLoadingGoals(false);
    }
  };

  // Efecto para re-ejecutar la búsqueda cuando cambia el filtro de banco
  useEffect(() => {
    if (goalRows.length > 0 && startDate && endDate) {
      fetchDepositsAndCompare();
    }
  }, [bancoFiltro]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <AlertBanner />
        {/* Goals / Objectives card */}
        <VuiBox mb={3}>
          <Card sx={{ p: 2, mb: 2 }}>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="12px">
              <VuiTypography variant="lg" color="white">
                Metas por día de la semana
              </VuiTypography>
              <VuiBox display="flex" gap={1} alignItems="center">
                <VuiBox sx={{ width: "170px" }}>
                  <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
                    Desde
                  </VuiTypography>
                  <TextField
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        color: "white",
                        backgroundColor: "#1a1d21",
                        cursor: "pointer",
                        height: "40px",
                        borderRadius: "8px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 200, 83, 0.6)",
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00c853",
                        borderWidth: "2px",
                      },
                      "& input[type='date']::-webkit-calendar-picker-indicator": {
                        filter: "invert(1) brightness(1.5)",
                        cursor: "pointer",
                        width: "20px",
                        height: "20px",
                        marginLeft: "4px",
                      },
                      "& input[type='date']": {
                        colorScheme: "dark",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                      },
                      "& .MuiInputBase-input": {
                        cursor: "pointer",
                        padding: "8px 12px",
                      },
                    }}
                  />
                </VuiBox>
                <VuiBox sx={{ width: "170px" }}>
                  <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
                    Hasta
                  </VuiTypography>
                  <TextField
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        color: "white",
                        backgroundColor: "#1a1d21",
                        cursor: "pointer",
                        height: "40px",
                        borderRadius: "8px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 200, 83, 0.6)",
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00c853",
                        borderWidth: "2px",
                      },
                      "& input[type='date']::-webkit-calendar-picker-indicator": {
                        filter: "invert(1) brightness(1.5)",
                        cursor: "pointer",
                        width: "20px",
                        height: "20px",
                        marginLeft: "4px",
                      },
                      "& input[type='date']": {
                        colorScheme: "dark",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                      },
                      "& .MuiInputBase-input": {
                        cursor: "pointer",
                        padding: "8px 12px",
                      },
                    }}
                  />
                </VuiBox>
                <VuiButton color="info" size="small" onClick={fetchDepositsAndCompare} sx={{ ml: 2 }}>
                  Revisar
                </VuiButton>
              </VuiBox>
            </VuiBox>

            {/* Botones de filtro por banco */}
            <VuiBox
              display="flex"
              gap={2}
              mb={2}
              alignItems="center"
              flexWrap="wrap"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderRadius: "12px",
                p: 2,
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <VuiTypography color="white" fontSize="0.9rem" fontWeight="bold">
                Filtrar por banco:
              </VuiTypography>
              
              <VuiButton
                color={bancoFiltro === "" ? "info" : "secondary"}
                size="small"
                onClick={() => setBancoFiltro("")}
                sx={{
                  minWidth: "80px",
                  background: bancoFiltro === "" 
                    ? "linear-gradient(135deg, #0075FF 0%, #0056CC 100%)"
                    : "rgba(255, 255, 255, 0.1)",
                  fontWeight: bancoFiltro === "" ? "bold" : "normal",
                }}
              >
                Todos
              </VuiButton>

              <VuiButton
                color={bancoFiltro === "com.bcp.innovacxion.yapeapp" ? "success" : "secondary"}
                size="small"
                onClick={() => setBancoFiltro("com.bcp.innovacxion.yapeapp")}
                sx={{
                  minWidth: "100px",
                  background: bancoFiltro === "com.bcp.innovacxion.yapeapp"
                    ? "linear-gradient(135deg, #722D90 0%, #5A1E73 100%)"
                    : "rgba(255, 255, 255, 0.1)",
                  fontWeight: bancoFiltro === "com.bcp.innovacxion.yapeapp" ? "bold" : "normal",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img src={YapeLogo} alt="Yape" style={{ width: "18px", height: "18px" }} />
                Yape
              </VuiButton>

              <VuiButton
                color={bancoFiltro === "pe.com.interbank.mobilebanking" ? "warning" : "secondary"}
                size="small"
                onClick={() => setBancoFiltro("pe.com.interbank.mobilebanking")}
                sx={{
                  minWidth: "130px",
                  background: bancoFiltro === "pe.com.interbank.mobilebanking"
                    ? "linear-gradient(135deg, #00D9A5 0%, #00B88A 100%)"
                    : "rgba(255, 255, 255, 0.1)",
                  fontWeight: bancoFiltro === "pe.com.interbank.mobilebanking" ? "bold" : "normal",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img src={PlinLogo} alt="Plin" style={{ width: "18px", height: "18px" }} />
                Plin Interbank
              </VuiButton>

              <VuiButton
                color={bancoFiltro === "com.bbva.nxt_peru" ? "info" : "secondary"}
                size="small"
                onClick={() => setBancoFiltro("com.bbva.nxt_peru")}
                sx={{
                  minWidth: "110px",
                  background: bancoFiltro === "com.bbva.nxt_peru"
                    ? "linear-gradient(135deg, #004481 0%, #003366 100%)"
                    : "rgba(255, 255, 255, 0.1)",
                  fontWeight: bancoFiltro === "com.bbva.nxt_peru" ? "bold" : "normal",
                }}
              >
                Plin BBVA
              </VuiButton>
            </VuiBox>

            <VuiBox display="flex" gap={2} flexWrap="wrap">
              {WEEKDAYS.map((w, idx) => (
                <VuiBox key={w} sx={{ minWidth: 140 }}>
                  <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
                    {w}
                  </VuiTypography>
                  <TextField
                    type="number"
                    size="small"
                    value={goals[idx] ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? "" : Number(e.target.value);
                      const ng = { ...goals, [idx]: val };
                      saveGoals(ng);
                    }}
                    placeholder="S/. 0.00"
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    sx={{
                      "& .MuiInputBase-root": {
                        color: "white",
                        backgroundColor: "#1a1d21",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  />
                </VuiBox>
              ))}
            </VuiBox>
          </Card>
        </VuiBox>

        {/* Gráfico de Metas vs Totales */}
        {goalRows.length > 0 && (
          <VuiBox mt={3}>
            <Card sx={{ p: 3 }}>
              <VuiTypography variant="lg" color="white" mb={3}>
                📊 Metas vs Totales (Gráfico)
              </VuiTypography>
              <VuiBox sx={{ height: "350px" }}>
                <Bar
                  data={{
                    labels: goalRows.map((row) => {
                      const [y, m, d] = (row.Fecha || "").split("-");
                      return `${row.Día} ${d}/${m}`;
                    }),
                    datasets: [
                      {
                        label: "Objetivo",
                        data: goalRows.map(row => {
                          const val = row.Objetivo;
                          return val === '-' ? 0 : parseFloat(val.replace('S/. ', '').replace(/,/g, ''));
                        }),
                        backgroundColor: 'rgba(106, 13, 173, 0.6)',
                        borderColor: 'rgba(106, 13, 173, 1)',
                        borderWidth: 2,
                      },
                      {
                        label: "Total real",
                        data: goalRows.map(row => parseFloat(row.Total.replace('S/. ', '').replace(/,/g, ''))),
                        backgroundColor: 'rgba(0, 200, 83, 0.6)',
                        borderColor: 'rgba(0, 200, 83, 1)',
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: 'white',
                          font: {
                            size: 12,
                            weight: 'bold',
                          },
                        },
                      },
                      title: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            let label = context.dataset.label || '';
                            if (label) {
                              label += ': ';
                            }
                            label += 'S/. ' + context.parsed.y.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            return label;
                          }
                        }
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.7)',
                          callback: function(value) {
                            return 'S/. ' + value.toLocaleString('es-PE');
                          },
                        },
                        grid: {
                          color: 'rgba(255, 255, 255, 0.1)',
                          drawBorder: false,
                        },
                      },
                      x: {
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.7)',
                          maxRotation: 45,
                          minRotation: 45,
                        },
                        grid: {
                          color: 'rgba(255, 255, 255, 0.05)',
                          drawBorder: false,
                        },
                      },
                    },
                  }}
                />
              </VuiBox>
            </Card>
          </VuiBox>
        )}

        <VuiBox mt={3}>
          <Card>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="12px" p={2}>
              <VuiTypography variant="lg" color="white">
                Resultados de metas (por día)
              </VuiTypography>
              <VuiBox>
                <Chip label={loadingGoals ? "Cargando..." : `Fechas ${startDate} → ${endDate}`} color="info" />
              </VuiBox>
            </VuiBox>

            <VuiBox p={2} sx={{ "& th": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` } }}>
              <Table
                columns={[
                  { name: "Fecha", align: "left" },
                  { name: "Día", align: "left" },
                  { name: "Objetivo", align: "right" },
                  { name: "Total", align: "right" },
                  { name: "Diferencia", align: "right" },
                  { name: "Progreso", align: "center" },
                  { name: "Estado", align: "center" },
                ]}
                rows={goalRows}
              />
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

  export default Tables;
